import * as React from 'react';
import { motion } from 'framer-motion';
import { Truck, Plus, Search, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Modal } from '@/components/ui/modal';
import { RequireRole } from '@/lib/auth';
import { apiFetch } from '@/lib/api';

interface Supplier {
  id: string;
  company_name: string;
  cnpj: string;
  phone: string;
  contact_name: string;
  email: string | null;
  created_at: string;
  updated_at: string;
}

interface SupplierFormData {
  company_name: string;
  cnpj: string;
  phone: string;
  contact_name: string;
  email: string;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

function formatCNPJ(cnpj: string): string {
  const digits = cnpj.replace(/\D/g, '');
  if (digits.length === 14) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  }
  return cnpj;
}

function maskCNPJ(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

function maskPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  return digits
    .replace(/^(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2');
}

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = React.useState(value);
  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

function SuppliersPageContent() {
  const [suppliers, setSuppliers] = React.useState<Supplier[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [pagination, setPagination] = React.useState<Pagination>({ total: 0, page: 1, limit: 20, totalPages: 0 });
  const [modalOpen, setModalOpen] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState<Supplier | null>(null);
  const [editingSupplier, setEditingSupplier] = React.useState<Supplier | null>(null);
  const [formData, setFormData] = React.useState<SupplierFormData>({
    company_name: '',
    cnpj: '',
    phone: '',
    contact_name: '',
    email: '',
  });
  const [formErrors, setFormErrors] = React.useState<Partial<Record<keyof SupplierFormData, string>>>({});
  const [submitting, setSubmitting] = React.useState(false);

  const fetchSuppliers = React.useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '20');
      if (debouncedSearch) params.set('search', debouncedSearch);

      const response = await apiFetch(`/api/suppliers?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setSuppliers(data.data);
        setPagination(data.pagination);
      }
    } catch {
      toast.error('Erro ao carregar fornecedores');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  React.useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  function openCreateModal() {
    setEditingSupplier(null);
    setFormData({
      company_name: '',
      cnpj: '',
      phone: '',
      contact_name: '',
      email: '',
    });
    setFormErrors({});
    setModalOpen(true);
  }

  function openEditModal(supplier: Supplier) {
    setEditingSupplier(supplier);
    setFormData({
      company_name: supplier.company_name,
      cnpj: supplier.cnpj,
      phone: supplier.phone,
      contact_name: supplier.contact_name,
      email: supplier.email || '',
    });
    setFormErrors({});
    setModalOpen(true);
  }

  function validateForm(): boolean {
    const errors: Partial<Record<keyof SupplierFormData, string>> = {};
    if (!formData.company_name.trim()) errors.company_name = 'Razão social é obrigatória';
    if (!formData.cnpj.trim()) errors.cnpj = 'CNPJ é obrigatório';
    if (!formData.phone.trim()) errors.phone = 'Telefone é obrigatório';
    if (!formData.contact_name.trim()) errors.contact_name = 'Nome do contato é obrigatório';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const body = {
        company_name: formData.company_name,
        cnpj: formData.cnpj,
        phone: formData.phone,
        contact_name: formData.contact_name,
        email: formData.email || null,
      };

      if (editingSupplier) {
        const response = await apiFetch(`/api/suppliers/${editingSupplier.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.error || 'Erro ao atualizar');
        }
        toast.success('Fornecedor atualizado com sucesso');
      } else {
        const response = await apiFetch('/api/suppliers', {
          method: 'POST',
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.error || 'Erro ao criar');
        }
        toast.success('Fornecedor cadastrado com sucesso');
      }

      setModalOpen(false);
      fetchSuppliers(pagination.page);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro inesperado';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(supplier: Supplier) {
    try {
      const response = await apiFetch(`/api/suppliers/${supplier.id}`, { method: 'DELETE' });
      if (!response.ok && response.status !== 204) {
        const err = await response.json();
        throw new Error(err.error || 'Erro ao excluir');
      }
      toast.success('Fornecedor excluído com sucesso');
      setDeleteTarget(null);
      fetchSuppliers(pagination.page);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro inesperado';
      toast.error(message);
    }
  }

  const columns: Column<Supplier>[] = [
    {
      key: 'company_name',
      header: 'Razão Social',
      sortable: true,
      render: (row) => <span className="font-medium text-base">{row.company_name}</span>,
    },
    {
      key: 'cnpj',
      header: 'CNPJ',
      render: (row) => <span className="font-mono text-sm">{formatCNPJ(row.cnpj)}</span>,
    },
    {
      key: 'phone',
      header: 'Telefone',
      render: (row) => <span className="text-base">{row.phone}</span>,
    },
    {
      key: 'contact_name',
      header: 'Contato',
      render: (row) => <span className="text-base">{row.contact_name}</span>,
    },
    {
      key: 'email',
      header: 'E-mail',
      render: (row) => <span className="text-muted-foreground text-base">{row.email || '—'}</span>,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-24',
      render: (row) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => openEditModal(row)}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label={`Editar ${row.company_name}`}
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setDeleteTarget(row)}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
            aria-label={`Excluir ${row.company_name}`}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Truck className="h-6 w-6 text-primary" />
            Fornecedores
          </h1>
          <p className="mt-1 text-base text-muted-foreground">
            Gerencie o cadastro de fornecedores
          </p>
        </div>
        <Button onClick={openCreateModal}>
          <Plus className="h-4 w-4" />
          Novo Fornecedor
        </Button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por razão social, CNPJ ou contato..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-11 w-full rounded-lg border border-input bg-background pl-9 pr-3 text-base placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
          />
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={suppliers}
            getRowKey={(row) => row.id}
            emptyMessage="Nenhum fornecedor encontrado"
          />
          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {pagination.total} fornecedor(es) encontrado(s)
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => fetchSuppliers(pagination.page - 1)}
                >
                  Anterior
                </Button>
                <span className="text-sm text-muted-foreground">
                  Página {pagination.page} de {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => fetchSuppliers(pagination.page + 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingSupplier ? 'Editar Fornecedor' : 'Novo Fornecedor'}
        description={editingSupplier ? 'Atualize as informações do fornecedor' : 'Preencha os dados para cadastrar um novo fornecedor'}
        className="max-w-lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="supplier-company-name" className="mb-1.5 block text-sm font-medium text-muted-foreground">
              Razão Social *
            </label>
            <input
              id="supplier-company-name"
              type="text"
              value={formData.company_name}
              onChange={(e) => setFormData((p) => ({ ...p, company_name: e.target.value }))}
              className={`flex h-11 w-full rounded-lg border bg-background px-3 text-base transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${formErrors.company_name ? 'border-destructive' : 'border-input'}`}
            />
            {formErrors.company_name && <p className="mt-1 text-xs text-destructive">{formErrors.company_name}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="supplier-cnpj" className="mb-1.5 block text-sm font-medium text-muted-foreground">
                CNPJ *
              </label>
              <input
                id="supplier-cnpj"
                type="text"
                value={formData.cnpj}
                onChange={(e) => setFormData((p) => ({ ...p, cnpj: maskCNPJ(e.target.value) }))}
                placeholder="00.000.000/0000-00"
                maxLength={18}
                className={`flex h-11 w-full rounded-lg border bg-background px-3 text-base transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${formErrors.cnpj ? 'border-destructive' : 'border-input'}`}
              />
              {formErrors.cnpj && <p className="mt-1 text-xs text-destructive">{formErrors.cnpj}</p>}
            </div>
            <div>
              <label htmlFor="supplier-phone" className="mb-1.5 block text-sm font-medium text-muted-foreground">
                Telefone *
              </label>
              <input
                id="supplier-phone"
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData((p) => ({ ...p, phone: maskPhone(e.target.value) }))}
                placeholder="(00) 00000-0000"
                maxLength={15}
                className={`flex h-11 w-full rounded-lg border bg-background px-3 text-base transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${formErrors.phone ? 'border-destructive' : 'border-input'}`}
              />
              {formErrors.phone && <p className="mt-1 text-xs text-destructive">{formErrors.phone}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="supplier-contact" className="mb-1.5 block text-sm font-medium text-muted-foreground">
                Nome do Contato *
              </label>
              <input
                id="supplier-contact"
                type="text"
                value={formData.contact_name}
                onChange={(e) => setFormData((p) => ({ ...p, contact_name: e.target.value }))}
                className={`flex h-11 w-full rounded-lg border bg-background px-3 text-base transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary ${formErrors.contact_name ? 'border-destructive' : 'border-input'}`}
              />
              {formErrors.contact_name && <p className="mt-1 text-xs text-destructive">{formErrors.contact_name}</p>}
            </div>
            <div>
              <label htmlFor="supplier-email" className="mb-1.5 block text-sm font-medium text-muted-foreground">
                E-mail (opcional)
              </label>
              <input
                id="supplier-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                className="flex h-11 w-full rounded-lg border border-input bg-background px-3 text-base transition-all focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Salvando...' : editingSupplier ? 'Atualizar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-foreground mb-2">Excluir Fornecedor</h3>
            <p className="text-base text-muted-foreground mb-4">
              Tem certeza que deseja excluir o fornecedor <strong>{deleteTarget.company_name}</strong>?
              Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
              <Button variant="destructive" onClick={() => handleDelete(deleteTarget)}>
                <Trash2 className="h-4 w-4 mr-1" />
                Excluir
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function SuppliersPage() {
  return (
    <RequireRole roles={['gerente']}>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <SuppliersPageContent />
      </motion.div>
    </RequireRole>
  );
}
